
import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Node } from '@xyflow/react';
import { NodeData } from './initial-nodes';

interface ActorPropertiesPanelProps {
  node: Node<NodeData> | null;
  onClose: () => void;
  onSave: (nodeId: string, actorText: string, actorColor: string) => void;
}

export function ActorPropertiesPanel({
  node,
  onClose,
  onSave,
}: ActorPropertiesPanelProps) {
  const [actorText, setActorText] = useState(node?.data?.actorText || '');
  const [actorColor, setActorColor] = useState(node?.data?.actorColor || '#6b7280');

  // Mesma paleta do EdgePropertiesPanel
  const colorOptions = [
    { value: '#6b7280', label: 'Cinza', color: '#6b7280' },
    { value: '#3b82f6', label: 'Azul', color: '#3b82f6' },
    { value: '#10b981', label: 'Verde', color: '#10b981' },
    { value: '#f59e0b', label: 'Amarelo', color: '#f59e0b' },
    { value: '#ef4444', label: 'Vermelho', color: '#ef4444' },
    { value: '#8b5cf6', label: 'Roxo', color: '#8b5cf6' },
    { value: '#ec4899', label: 'Rosa', color: '#ec4899' }
  ];

  useEffect(() => {
    if (node) {
      setActorText(node.data?.actorText || '');
      setActorColor(node.data?.actorColor || '#6b7280');
    }
  }, [node]);

  if (!node) return null;

  const handleSave = () => {
    onSave(node.id, actorText, actorColor);
    onClose();
  };

  return (
    <div className="fixed top-0 right-0 w-80 h-full bg-white border-l border-gray-200 shadow-lg z-50 overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Propriedades do Ator</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="p-4 space-y-4">
        <div>
          <Label htmlFor="actor-text" className="block text-sm font-medium text-gray-700 mb-2">
            Texto
          </Label>
          <Input
            id="actor-text"
            value={actorText}
            onChange={(e) => setActorText(e.target.value)}
            placeholder="Nome do ator..."
            className="w-full"
          />
        </div>

        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-2">
            Cor do Ícone
          </Label>
          <div className="flex flex-wrap gap-2">
            {colorOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setActorColor(option.value)}
                className={`w-8 h-8 border rounded transition-all ${
                  actorColor === option.value 
                    ? 'border-[#05445e] border-2' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  backgroundColor: option.color
                }}
                title={option.label}
              />
            ))}
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-[#05445e] hover:bg-[#189ab4]"
          >
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
